import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ConnectFour.css';

const ConnectFour = () => {
  const [board, setBoard] = useState(() => Array(6).fill().map(() => Array(7).fill(0)));
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [winningCells, setWinningCells] = useState([]);

  const dropPiece = (col) => {
    if (gameOver) return;

    const newBoard = board.map(row => [...row]);
    
    // Find the lowest empty row in the column
    for (let row = 5; row >= 0; row--) {
      if (newBoard[row][col] === 0) {
        newBoard[row][col] = currentPlayer;
        setBoard(newBoard);
        
        // Check for win
        const winResult = checkWin(newBoard, row, col, currentPlayer);
        if (winResult.hasWin) {
          setWinner(currentPlayer);
          setWinningCells(winResult.winningCells);
          setGameOver(true);
        } else if (isBoardFull(newBoard)) {
          setGameOver(true);
          setWinner('tie');
        } else {
          setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
        }
        break;
      }
    }
  };

  const checkWin = (board, row, col, player) => {
    const directions = [
      [0, 1],   // horizontal
      [1, 0],   // vertical
      [1, 1],   // diagonal /
      [1, -1]   // diagonal \
    ];

    for (const [dRow, dCol] of directions) {
      const cells = [[row, col]]; // Start with the current cell
      
      // Check in positive direction
      let r = row + dRow;
      let c = col + dCol;
      while (r >= 0 && r < 6 && c >= 0 && c < 7 && board[r][c] === player) {
        cells.push([r, c]);
        r += dRow;
        c += dCol;
      }
      
      // Check in negative direction
      r = row - dRow;
      c = col - dCol;
      while (r >= 0 && r < 6 && c >= 0 && c < 7 && board[r][c] === player) {
        cells.push([r, c]);
        r -= dRow;
        c -= dCol;
      }
      
      if (cells.length >= 4) {
        return { hasWin: true, winningCells: cells };
      }
    }
    
    return { hasWin: false, winningCells: [] };
  };

  const isBoardFull = (board) => {
    return board[0].every(cell => cell !== 0);
  };

  const resetGame = () => {
    setBoard(Array(6).fill().map(() => Array(7).fill(0)));
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
    setWinningCells([]);
  };

  const isWinningCell = (row, col) => {
    return winningCells.some(([r, c]) => r === row && c === col);
  };

  const canDropInColumn = (col) => {
    return !gameOver && board[0][col] === 0;
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🔴 Connect Four</h1>
        <div className="game-instructions">
          Drop your pieces into columns to get four in a row horizontally, vertically, or diagonally!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          {gameOver ? (
            winner === 'tie' ? (
              "It's a Tie! 🤝"
            ) : (
              `Player ${winner} Wins! 🎉`
            )
          ) : (
            `Player ${currentPlayer}'s Turn ${currentPlayer === 1 ? '🔴' : '🟡'}`
          )}
        </div>

        <div className="game-play-area">
          <div className="connect-four-board">
            <div className="column-indicators">
              {Array(7).fill().map((_, col) => (
                <div
                  key={col}
                  className={`column-indicator ${canDropInColumn(col) ? 'droppable' : 'blocked'}`}
                  onClick={() => dropPiece(col)}
                >
                  ↓
                </div>
              ))}
            </div>
            
            <div className="board-grid">
              {board.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`connect-four-cell ${
                      cell === 1 ? 'player1' : cell === 2 ? 'player2' : 'empty'
                    } ${isWinningCell(rowIndex, colIndex) ? 'winning' : ''}`}
                    onClick={() => dropPiece(colIndex)}
                  >
                    <div className="piece">
                      {cell === 1 ? '🔴' : cell === 2 ? '🟡' : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={resetGame}>
          Reset Game
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default ConnectFour;